import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  AlertTriangle,
  Check,
  Loader2,
  Microscope,
  RotateCcw,
  WifiOff,
  X,
} from 'lucide-react';
import { normalizeCaseWikiContextUserInputs } from '@hector/shared-types';
import { cn } from '@/lib/utils';
import type { CaseWikiResetVerdict } from '../../types/session.types';

interface ResetAnalysisCardProps {
  caseId: string;
  caseTitle?: string | null;
  clientName?: string | null;
  /** Nonce backend à renvoyer verbatim pour autoriser uniquement ce reset. */
  requestId: string;
  caseWikiContextUserInputs?: string;
  /** Le verdict backend, et jamais le succès d'envoi WS, ferme la demande. */
  result?: CaseWikiResetVerdict;
  onRespond?: (payload: {
    caseId: string;
    accepted: boolean;
    requestId: string;
    caseWikiContextUserInputs?: string;
  }) => boolean;
}

type ResetDecision = 'pending' | 'submitting' | 'succeeded' | 'dismissed' | 'expired';
type SubmittedAction = 'reset' | 'cancel' | null;

/**
 * Demande une validation explicite uniquement pour le reset destructif.
 * Le lancement normal de l'Analyse avancée est autonome et n'utilise pas cette UI.
 */
export function ResetAnalysisCard({
  caseId,
  caseTitle,
  clientName,
  requestId,
  caseWikiContextUserInputs,
  result,
  onRespond,
}: ResetAnalysisCardProps) {
  const [decision, setDecision] = useState<ResetDecision>('pending');
  const [submittedAction, setSubmittedAction] = useState<SubmittedAction>(null);
  const [draftContext, setDraftContext] = useState(caseWikiContextUserInputs ?? '');
  const [sendFailed, setSendFailed] = useState(false);
  const [expiredReason, setExpiredReason] = useState<'timedOut' | 'error' | null>(null);

  useEffect(() => {
    setDecision('pending');
    setSubmittedAction(null);
    setSendFailed(false);
    setExpiredReason(null);
  }, [requestId]);

  useEffect(() => {
    setDraftContext(caseWikiContextUserInputs ?? '');
  }, [requestId, caseWikiContextUserInputs]);

  useEffect(() => {
    if (!result) return;
    if (result.launched) {
      setDecision('succeeded');
      return;
    }
    if (result.declined) {
      setDecision('dismissed');
      return;
    }
    setExpiredReason(result.error ? 'error' : 'timedOut');
    setDecision('expired');
  }, [requestId, result]);

  const targetDetails = caseTitle || clientName ? (
    <div className="flex flex-col gap-0.5 text-xs text-grey-500">
      {caseTitle ? <span>Dossier ciblé: {caseTitle}</span> : null}
      {clientName ? <span>Client: {clientName}</span> : null}
    </div>
  ) : null;

  const send = (accepted: boolean): boolean => {
    if (!onRespond) return false;
    const normalizedContext = normalizeCaseWikiContextUserInputs(draftContext);
    const sent = onRespond({
      caseId,
      accepted,
      requestId,
      // WHY: an empty string explicitly records that the lawyer cleared the
      // agent proposal; omission remains reserved for legacy payloads.
      ...(accepted ? { caseWikiContextUserInputs: normalizedContext ?? '' } : {}),
    });
    setSendFailed(!sent);
    return sent;
  };

  const handleReset = () => {
    if (decision !== 'pending') return;
    // WHY: a successful socket write is not a reset verdict. Stay in the
    // intermediate state until the correlated backend result arrives.
    if (send(true)) {
      setSubmittedAction('reset');
      setDecision('submitting');
    }
  };

  const handleCancel = () => {
    if (decision !== 'pending') return;
    // WHY: the socket write only acknowledges transport. Keep the card in a
    // pending state until the backend closes this exact nonce as declined.
    if (send(false)) {
      setSubmittedAction('cancel');
      setDecision('submitting');
    }
  };

  if (decision === 'dismissed') {
    return (
      <CompactStatus
        targetDetails={targetDetails}
        icon={<X className="h-3.5 w-3.5 flex-shrink-0 text-grey-400" />}
        label="Réinitialisation annulée"
      />
    );
  }

  if (decision === 'succeeded') {
    const label = result?.alreadyRunning
      ? 'Analyse déjà en cours : aucun reset effectué'
      : 'Réinitialisation lancée';
    return (
      <CompactStatus
        targetDetails={targetDetails}
        icon={<Check className="h-3.5 w-3.5 flex-shrink-0 text-grey-400" />}
        label={label}
      />
    );
  }

  if (decision === 'expired') {
    const label = expiredReason === 'error'
      ? result?.userMessage ?? 'Réinitialisation impossible : redemandez à Hector.'
      : 'Demande expirée : redemandez à Hector pour réinitialiser l’analyse.';
    return (
      <CompactStatus
        targetDetails={targetDetails}
        icon={<AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-amber-500" />}
        label={label}
      />
    );
  }

  if (decision === 'submitting') {
    return (
      <CompactStatus
        targetDetails={targetDetails}
        icon={<Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-grey-400" />}
        label={submittedAction === 'cancel' ? 'Annulation en cours…' : 'Validation de la demande en cours…'}
      />
    );
  }

  const isInteractive = Boolean(onRespond);
  return (
    <div className="overflow-hidden rounded-lg border border-grey-200 bg-white">
      <div className="flex items-center gap-2 border-b border-grey-100 bg-grey-50/60 px-3.5 py-2">
        <Microscope className="h-3.5 w-3.5 flex-shrink-0 text-grey-400" />
        <span className="text-xs font-medium text-grey-600">Analyse avancée du dossier</span>
      </div>

      <div className="px-3.5 py-3">
        <p className="mb-2 text-sm font-medium text-grey-800">
          Réinitialiser l&apos;Analyse avancée ?
        </p>
        <p className="mb-3 text-sm text-grey-700">
          Hector supprimera l&apos;analyse actuelle puis repartira de zéro à partir des pièces du dossier.
        </p>
        {targetDetails ? (
          <div className="mb-3 rounded-md border border-grey-100 bg-grey-50 px-3 py-2">
            {targetDetails}
          </div>
        ) : null}
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <AlertTriangle className="mt-px h-3.5 w-3.5 flex-shrink-0" />
          Le contenu de l&apos;analyse actuelle sera définitivement remplacé.
        </div>
        <div className="mt-3">
          <textarea
            aria-label="Contexte du reset de l’analyse avancée"
            value={draftContext}
            onChange={(event) => setDraftContext(event.target.value)}
            rows={4}
            disabled={!isInteractive}
            className="w-full resize-y rounded-lg border border-grey-200 bg-grey-50 px-3 py-2 text-sm text-grey-800 outline-none transition focus:border-grey-300 focus:bg-white disabled:cursor-not-allowed disabled:opacity-60"
            placeholder="Ajoutez un contexte avocat optionnel pour orienter la nouvelle analyse."
          />
          <p className="mt-1.5 text-xs text-grey-400">
            Ce contexte sera utilisé uniquement après votre validation du reset.
          </p>
        </div>
      </div>

      {sendFailed ? (
        <div className="flex items-center gap-1.5 border-t border-amber-200 bg-amber-50 px-3.5 py-2 text-xs text-amber-700">
          <WifiOff className="h-3.5 w-3.5 flex-shrink-0" />
          Connexion perdue, réessayez dans un instant.
        </div>
      ) : null}

      <div className="flex justify-end gap-2 border-t border-grey-100 px-3.5 py-2">
        <button
          type="button"
          onClick={handleCancel}
          disabled={!isInteractive}
          className="flex items-center gap-1.5 rounded-lg border border-grey-200 px-4 py-2 text-sm font-medium text-grey-600 transition hover:bg-grey-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <X className="h-3.5 w-3.5" />
          Annuler
        </button>
        <button
          type="button"
          onClick={handleReset}
          disabled={!isInteractive}
          className={cn(
            'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-150',
            'bg-red-600 text-white hover:bg-red-700 active:scale-[0.98]',
            'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-red-600',
          )}
        >
          <RotateCcw className="h-4 w-4" />
          Réinitialiser l&apos;analyse
        </button>
      </div>
    </div>
  );
}

function CompactStatus({
  targetDetails,
  icon,
  label,
}: {
  targetDetails: ReactNode;
  icon: ReactNode;
  label: string;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="overflow-hidden rounded-lg border border-grey-200 bg-white"
    >
      <div className="flex items-start gap-2 px-3.5 py-2">
        <Microscope className="mt-px h-3.5 w-3.5 flex-shrink-0 text-grey-400" />
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-grey-600">Analyse avancée du dossier</span>
          {targetDetails}
        </div>
        <span className="ml-auto inline-flex max-w-md items-start gap-1.5 text-right text-xs font-medium text-grey-500">
          {icon}
          {label}
        </span>
      </div>
    </div>
  );
}
